import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import BackEndUrl from "../backURL";
import OLYMPIAD from "../olympiad";
import { formatDate } from "../season";

const backend = BackEndUrl.domain;

// Categories, exam centers and the "age as at" date come from the current season (admin panel)
const AdmissionForm = ({ season }) => {
  const [loading, setLoading] = useState(false); // 🟢 Loading state

  const validationSchema = Yup.object().shape({
    CATEGORY: Yup.string().required("Category is required"),
    FULLNAME: Yup.string().required("Full name is required"),
    NAMEINITIALS: Yup.string().required("Name with initials is required"),
    DOB: Yup.date().required("Date of Birth is required"),
    GENDER: Yup.string().required("Gender is required"),
    AGE: Yup.string().required("Age is required"),
    SCHOOL: Yup.string().required("School name is required"),
    GRADE: Yup.string().required("Grade is required"),
    SCHOOLADDRESS: Yup.string().required("School address is required"),
    HOMEADDRESS: Yup.string().required("Home address is required"),
    WHATSAPPNUMBER: Yup.string().required("WhatsApp number is required"),
    PERSONALEMAIL: Yup.string()
      .email("Invalid email")
      .required("Personal email is required"),
    LANGUAGE: Yup.string().required("Language is required"),
    EXAMCENTER: Yup.string().required("Examination center is required"),
    PROFILEPICTURE: Yup.mixed().required("Profile picture is required"),
    BANKRECEIPTURL: Yup.mixed().required("Bank receipt is required"),
    DIGITALSIGNATURE: Yup.string().required("Digital signature is required"),
  });

  return (
    <Formik
      initialValues={{
        CATEGORY: "",
        FULLNAME: "",
        NAMEINITIALS: "",
        DOB: "",
        GENDER: "",
        AGE: "",
        SCHOOL: "",
        GRADE: "",
        SCHOOLADDRESS: "",
        HOMEADDRESS: "",
        WHATSAPPNUMBER: "",
        PERSONALEMAIL: "",
        LANGUAGE: "",
        EXAMCENTER: "",
        EMAIL: "",
        PROFILEPICTURE: null,
        BANKRECEIPTURL: null,
        DIGITALSIGNATURE: "",
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        const formData = new FormData();
        for (const key in values) {
          formData.append(key, values[key]);
        }

        try {
          setLoading(true); // start loading
          const response = await axios.post(
            `${backend}${OLYMPIAD.submitPath}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          );
          console.log("Form submitted successfully:", response.data);
          alert(
            "✅ Thank you! Your submission was successful.\n\n" +
              "Your admission card has been emailed to you as a PDF attachment. Please print it and bring it to the examination center.\n\n" +
              "If you do not see the email within a few minutes, please check your Spam/Junk folder.",
          );
          resetForm();
        } catch (error) {
          console.error("Error submitting form:", error);
          if (error.response) {
            alert(error.response.data || "Server error");
          } else if (error.request) {
            alert("No response received from server. Please try again later.");
          } else {
            alert("An error occurred. Please try again.");
          }
        } finally {
          setLoading(false); // stop loading
        }
      }}
    >
      {({ setFieldValue, errors, touched }) => (
        <Form className="space-y-4 relative">
          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center z-10">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-blue-700 font-medium">
                Submitting your form, please wait...
              </p>
            </div>
          )}

          {/* ===================== FORM START ===================== */}

          <div className="flex flex-col">
            <label htmlFor="CATEGORY" className="mb-1 text-sm font-medium">
              Name of the Olympiad that you want to apply
            </label>
            <Field
              as="select"
              id="CATEGORY"
              name="CATEGORY"
              className="p-2 border rounded"
            >
              <option value="">Select Category</option>
              {season.categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </Field>
            {errors.CATEGORY && touched.CATEGORY && (
              <div className="text-red-500">{errors.CATEGORY}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="FULLNAME" className="mb-1 text-sm font-medium">
              Name in full
            </label>
            <Field
              id="FULLNAME"
              name="FULLNAME"
              className="p-2 border rounded"
            />
            {errors.FULLNAME && touched.FULLNAME && (
              <div className="text-red-500">{errors.FULLNAME}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="NAMEINITIALS" className="mb-1 text-sm font-medium">
              Name with initials
            </label>
            <Field
              id="NAMEINITIALS"
              name="NAMEINITIALS"
              className="p-2 border rounded"
            />
            {errors.NAMEINITIALS && touched.NAMEINITIALS && (
              <div className="text-red-500">{errors.NAMEINITIALS}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="DOB" className="mb-1 text-sm font-medium">
              Date of Birth
            </label>
            <Field
              id="DOB"
              name="DOB"
              type="date"
              className="p-2 border rounded"
            />
            {errors.DOB && touched.DOB && (
              <div className="text-red-500">{errors.DOB}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="GENDER" className="mb-1 text-sm font-medium">
              Gender
            </label>
            <Field
              as="select"
              id="GENDER"
              name="GENDER"
              className="p-2 border rounded"
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Field>
            {errors.GENDER && touched.GENDER && (
              <div className="text-red-500">{errors.GENDER}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="AGE" className="mb-1 text-sm font-medium">
              Age as at {formatDate(season.ageAsAt)} (Write in year/months/days order)
            </label>
            <Field id="AGE" name="AGE" className="p-2 border rounded" />
            {errors.AGE && touched.AGE && (
              <div className="text-red-500">{errors.AGE}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="SCHOOL" className="mb-1 text-sm font-medium">
              Name of the school
            </label>
            <Field id="SCHOOL" name="SCHOOL" className="p-2 border rounded" />
            {errors.SCHOOL && touched.SCHOOL && (
              <div className="text-red-500">{errors.SCHOOL}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="GRADE" className="mb-1 text-sm font-medium">
              Your Grade
            </label>
            <Field id="GRADE" name="GRADE" className="p-2 border rounded" />
            {errors.GRADE && touched.GRADE && (
              <div className="text-red-500">{errors.GRADE}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="SCHOOLADDRESS" className="mb-1 text-sm font-medium">
              Address of the school
            </label>
            <Field
              id="SCHOOLADDRESS"
              name="SCHOOLADDRESS"
              className="p-2 border rounded"
            />
            {errors.SCHOOLADDRESS && touched.SCHOOLADDRESS && (
              <div className="text-red-500">{errors.SCHOOLADDRESS}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="HOMEADDRESS" className="mb-1 text-sm font-medium">
              Contact address (Home)
            </label>
            <Field
              id="HOMEADDRESS"
              name="HOMEADDRESS"
              className="p-2 border rounded"
            />
            {errors.HOMEADDRESS && touched.HOMEADDRESS && (
              <div className="text-red-500">{errors.HOMEADDRESS}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="WHATSAPPNUMBER"
              className="mb-1 text-sm font-medium"
            >
              WhatsApp Telephone number
            </label>
            <Field
              id="WHATSAPPNUMBER"
              name="WHATSAPPNUMBER"
              className="p-2 border rounded"
            />
            {errors.WHATSAPPNUMBER && touched.WHATSAPPNUMBER && (
              <div className="text-red-500">{errors.WHATSAPPNUMBER}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="PERSONALEMAIL" className="mb-1 text-sm font-bold">
              E-mail address 01 (Admission will be sent to this email)
            </label>
            <Field
              id="PERSONALEMAIL"
              name="PERSONALEMAIL"
              className="p-2 border rounded"
            />
            {errors.PERSONALEMAIL && touched.PERSONALEMAIL && (
              <div className="text-red-500">{errors.PERSONALEMAIL}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="EMAIL" className="mb-1 text-sm font-medium">
              Email address 02 (Admission will be sent to this email)
            </label>
            <Field id="EMAIL" name="EMAIL" className="p-2 border rounded" />
          </div>

          <div className="flex flex-col">
            <label htmlFor="LANGUAGE" className="mb-1 text-sm font-medium">
              Preferred medium of instruction
            </label>
            <Field
              as="select"
              id="LANGUAGE"
              name="LANGUAGE"
              className="p-2 border rounded"
            >
              <option value="">Select Language</option>
              <option value="E">English</option>
              <option value="S">Sinhala</option>
              <option value="T">Tamil</option>
            </Field>
            {errors.LANGUAGE && touched.LANGUAGE && (
              <div className="text-red-500">{errors.LANGUAGE}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label htmlFor="EXAMCENTER" className="mb-1 text-sm font-medium">
              Preferred Examination Center/University
            </label>
            <Field
              as="select"
              id="EXAMCENTER"
              name="EXAMCENTER"
              className="p-2 border rounded"
            >
              <option value="">Select Examination Center</option>
              {season.centers.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Field>
            {errors.EXAMCENTER && touched.EXAMCENTER && (
              <div className="text-red-500">{errors.EXAMCENTER}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="PROFILEPICTURE"
              className="mb-1 text-sm font-medium"
            >
              Upload your passport size Photograph here (JPEG, PNG or iPhone
              HEIC photo, less than 10 MB)
            </label>
            <input
              id="PROFILEPICTURE"
              name="PROFILEPICTURE"
              type="file"
              accept="image/jpeg, image/png, image/heic, image/heif, .heic, .heif"
              onChange={(event) =>
                setFieldValue("PROFILEPICTURE", event.currentTarget.files[0])
              }
              className="p-2 border rounded"
            />
            {errors.PROFILEPICTURE && touched.PROFILEPICTURE && (
              <div className="text-red-500">{errors.PROFILEPICTURE}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="BANKRECEIPTURL"
              className="mb-1 text-sm font-medium"
            >
              Upload a copy of the deposit slip or fund transfer receipt
            </label>
            <input
              id="BANKRECEIPTURL"
              name="BANKRECEIPTURL"
              type="file"
              onChange={(event) =>
                setFieldValue("BANKRECEIPTURL", event.currentTarget.files[0])
              }
              className="p-2 border rounded"
            />
            {errors.BANKRECEIPTURL && touched.BANKRECEIPTURL && (
              <div className="text-red-500">{errors.BANKRECEIPTURL}</div>
            )}
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="DIGITALSIGNATURE"
              className="mb-1 text-sm font-medium"
            >
              I state that the above particulars are true and correct. Digital
              Signature of the applicant (or write your name here)
            </label>
            <Field
              id="DIGITALSIGNATURE"
              name="DIGITALSIGNATURE"
              className="p-2 border rounded"
            />
            {errors.DIGITALSIGNATURE && touched.DIGITALSIGNATURE && (
              <div className="text-red-500">{errors.DIGITALSIGNATURE}</div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`p-2 w-full text-white rounded transition 
              ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </Form>
      )}
    </Formik>
  );
};

export default AdmissionForm;
